/**
 * "Provide details": the office answers our team's Details Needed checklist on
 * a breakdown request. The patient and the policy as we hold them, what our
 * team asked for (and said), then one box per asked detail — prefilled with
 * today's value, each skippable with "I don't have this" — a reply note, and
 * Send. The server validates every answer together and names any box that
 * needs fixing (fieldErrors).
 */
import React, { useEffect, useState } from "react";
import { Button, Checkbox, DatePicker, Input, Modal, Select, Tag } from "antd";
import dayjs from "dayjs";

import { controller as rootController } from "../../controller";
import PatientInfoCard from "../TreatmentPlans/component/shared/PatientInfoCard";
import { phoneDigits } from "./newPatient";
import { buildPayload, canSubmit, initialDrafts, isAnswered, pmsHint } from "./infoAskReply";
import { OTHER_LABEL } from "./infoAsk";
import { policyLine } from "./rowContext";

function whenAsked(iso) {
  if (!iso) return "";
  const d = dayjs(iso);
  return d.isValid() ? d.format("MMM D, YYYY") : "";
}

function AskedField({ field, value, skipped, error, carriers, onChange, onSkip }) {
  const disabled = skipped;
  let control;
  if (field.input === "address") {
    const parts = value || {};
    const set = function (part) {
      return function (e) { onChange(Object.assign({}, parts, { [part]: e.target.value })); };
    };
    control = (
      <div className="ir-ask-address">
        <Input placeholder="Street address" value={parts.address || ""} onChange={set("address")} disabled={disabled}
          aria-label={field.label + " street"} />
        <div className="ir-ask-address-row">
          <Input placeholder="City" value={parts.city || ""} onChange={set("city")} disabled={disabled} aria-label={field.label + " city"} />
          <Input placeholder="Province / state" value={parts.state || ""} onChange={set("state")} disabled={disabled} aria-label={field.label + " state"} />
          <Input placeholder="Postal code" value={parts.zip_code || ""} onChange={set("zip_code")} disabled={disabled} aria-label={field.label + " postal code"} />
        </div>
      </div>
    );
  } else if (field.input === "date") {
    const parsed = value ? dayjs(String(value).split("T")[0]) : null;
    control = (
      <DatePicker format="YYYY-MM-DD" value={parsed && parsed.isValid() ? parsed : null} disabled={disabled}
        onChange={function (d) { onChange(d ? d.format("YYYY-MM-DD") : ""); }} aria-label={field.label} style={{ width: "100%" }} />
    );
  } else if (field.input === "select") {
    control = (
      <Select allowClear value={value || undefined} disabled={disabled} aria-label={field.label} style={{ width: "100%" }}
        options={(field.options || []).map(function (o) { return { value: o[0], label: o[1] }; })}
        onChange={function (v) { onChange(v || ""); }} />
    );
  } else if (field.input === "carrier") {
    control = (
      <Select showSearch optionFilterProp="label" allowClear value={value || undefined} disabled={disabled}
        aria-label={field.label} style={{ width: "100%" }} options={carriers} placeholder="Pick the carrier"
        onChange={function (v) { onChange(v || ""); }} />
    );
  } else {
    control = (
      <Input value={value || ""} disabled={disabled} aria-label={field.label}
        type={field.input === "email" ? "email" : "text"}
        onChange={function (e) { onChange(field.input === "phone" ? phoneDigits(e.target.value) : e.target.value); }} />
    );
  }
  return (
    <div className={"ir-ask-field" + (error ? " has-error" : "")}>
      <div className="ir-ask-field-head">
        <span className="ir-ask-field-label">{field.label}</span>
        <Checkbox checked={skipped} onChange={function (e) { onSkip(e.target.checked); }}>I don't have this</Checkbox>
      </div>
      {control}
      {error ? <div className="ir-ask-field-error">{error}</div> : null}
    </div>
  );
}

export default function InfoAskReplyModal({ row, busy, fieldErrors, onCancel, onSubmit }) {
  const ask = (row && row.info_ask) || { fields: [] };
  const [drafts, setDrafts] = useState(function () { return initialDrafts(ask); });
  const [skipped, setSkipped] = useState([]);
  const [note, setNote] = useState("");
  const [carriers, setCarriers] = useState([]);
  const needsCarriers = ask.fields.some(function (f) { return f.input === "carrier"; });

  useEffect(function () {
    if (!needsCarriers) return undefined;
    let cancelled = false;
    (async function () {
      try {
        const res = await rootController.listInsuranceProviders();
        if (!cancelled && res && Array.isArray(res.data)) {
          setCarriers(res.data.filter(function (c) { return c.is_active; })
            .map(function (c) { return { value: c.code, label: c.name }; }));
        }
      } catch (e) {
        console.error("Failed to load carriers", e);
      }
    })();
    return function () { cancelled = true; };
  }, [needsCarriers]);

  const errors = fieldErrors || {};
  const ready = canSubmit(ask, drafts, skipped, note);
  const hint = pmsHint(row && row.patient);
  const asksPatient = ask.fields.some(function (f) { return f.group === "patient"; });

  return (
    <Modal
      open={!!row}
      title="Provide the details our team asked for"
      width={640}
      onCancel={onCancel}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={busy}>Cancel</Button>,
        <Button key="send" type="primary" loading={busy} disabled={!ready}
          onClick={function () { onSubmit(buildPayload(ask, drafts, skipped, note)); }}>
          Send to our team
        </Button>,
      ]}
    >
      <div className="ir-ask">
        <PatientInfoCard patient={row && row.patient} />
        <div className="ir-ask-policy">
          <span className="ir-ask-eyebrow">Policy</span>
          <span>{policyLine(row && row.policy) || "—"}</span>
        </div>

        <div className="ir-ask-needs" data-testid="info-ask-needs">
          <div className="ir-ask-eyebrow">
            What our team needs{ask.asked_at ? " · asked " + whenAsked(ask.asked_at) : ""}
          </div>
          <div className="ir-ask-chips">
            {ask.fields.map(function (f) { return <Tag key={f.key}>{f.label}</Tag>; })}
            {ask.other_text ? <Tag>{OTHER_LABEL}: {ask.other_text}</Tag> : null}
          </div>
          {ask.note ? <div className="ir-ask-note">{ask.note}</div> : null}
        </div>

        {ask.fields.map(function (field) {
          return (
            <AskedField
              key={field.key}
              field={field}
              value={drafts[field.key]}
              skipped={skipped.indexOf(field.key) !== -1}
              error={errors[field.key]}
              carriers={carriers}
              onChange={function (v) { setDrafts(function (prev) { return Object.assign({}, prev, { [field.key]: v }); }); }}
              onSkip={function (on) {
                setSkipped(function (prev) {
                  return on ? prev.concat(prev.indexOf(field.key) === -1 ? [field.key] : [])
                    : prev.filter(function (k) { return k !== field.key; });
                });
              }}
            />
          );
        })}

        <Input.TextArea
          rows={3}
          value={note}
          onChange={function (e) { setNote(e.target.value); }}
          placeholder={ask.fields.some(function (f) { return skipped.indexOf(f.key) !== -1 || !isAnswered(f, drafts[f.key]); })
            ? "Tell our team what you couldn't provide, and why"
            : "Anything else our team should know (optional)"}
          maxLength={2000}
          aria-label="Reply note"
        />
        {errors.note ? <div className="ir-ask-field-error">{errors.note}</div> : null}
        {asksPatient && hint ? <div className="ir-ask-hint">{hint}</div> : null}
      </div>
    </Modal>
  );
}
